import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
  InputGroup,
  Modal,
} from "react-bootstrap";
import {
  FaTelegram,
  FaKey,
  FaCopy,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaSyncAlt,
  FaInfoCircle,
  FaExclamationTriangle,
  FaWhatsapp,
  FaDownload,
} from "react-icons/fa";
import { getFirestore, collection, query, where, limit, getDocs, getDoc, doc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../../firebase/credenciales";
import BotonManualHeader from "../../components/BotonManualHeader";
import { checkPermission } from "../../functions/checkPermission";
import QRCode from "qrcode";

const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, "us-central1");

const TZ_LIMA = "America/Lima";

/**
 * Username del bot Neurum Time en Telegram (sin @). Genera los deep links
 * t.me/<bot>?start=vincular-CODIGO que alimentan el QR de vinculación.
 * Vacío → se ocultan QR/enlace y queda el /vincular manual.
 */
const BOT_USERNAME = "NeurumTimeBot";

function formatearFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-PE", {
      timeZone: TZ_LIMA,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
}

function estadoCodigo(data) {
  if (data.usado === true) return { etiqueta: "Usado", variante: "success" };
  const expira = data.expiraAt ? new Date(data.expiraAt).getTime() : 0;
  if (expira && expira < Date.now()) return { etiqueta: "Expirado", variante: "secondary" };
  return { etiqueta: "Vigente", variante: "info" };
}

/** Normaliza un teléfono peruano al formato internacional de WhatsApp. */
function phoneWa(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("51") && d.length === 11) return d;
  if (d.length === 9) return `51${d}`;
  return d;
}

/** Mensaje listo para WhatsApp (es-PE) con el deep link de activación. */
function mensajeWhatsapp({ nombre, link, expiraAt }) {
  if (!link) return "";
  return (
    `Hola ${nombre}, para activar tu marcación de asistencia por Telegram toca este enlace:\n` +
    `${link}\n` +
    `Se activa solo, sin tener que escribir nada. Válido hasta ${formatearFecha(expiraAt)}.`
  );
}

/**
 * ¿Puede generar códigos de vinculación?
 * Dos caminos (espejo server-side de la callable): permiso de configuración
 * de asistencia O rol de supervisión (superadmin/admin/supervisor/asistente/
 * rh + variantes rrhh) — los asistentes no requieren el permiso granular.
 */
const ROLES_CODIGO_TELEGRAM = new Set([
  "superadmin",
  "admin",
  "administrador",
  "supervisor",
  "asistente",
  "asistente_rh",
  "asistente_rrhh",
  "rh",
  "rrhh",
]);

export function puedeGenerarCodigosTelegram(usuario) {
  if (!usuario) return false;
  if (checkPermission(usuario, "asistencia", "CONFIGURACION")) return true;
  const rol = String(usuario.rol || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ROLES_CODIGO_TELEGRAM.has(rol);
}

function TelegramAsistencia({ usuario }) {
  const rucActual = usuario?.ruc || usuario?.rucEmpresa || "";

  const [dni, setDni] = useState("");
  const [ruc, setRuc] = useState(rucActual);
  const [procesando, setProcesando] = useState(false);
  const [codigoGenerado, setCodigoGenerado] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrError, setQrError] = useState(false);
  const [trabajador, setTrabajador] = useState(null);
  const [pendienteConfirmacion, setPendienteConfirmacion] = useState(null);
  const [error, setError] = useState("");

  const [codigos, setCodigos] = useState([]);
  const [vinculaciones, setVinculaciones] = useState([]);
  const [nombres, setNombres] = useState({});
  const [cargandoListas, setCargandoListas] = useState(false);

  useEffect(() => {
    if (rucActual) setRuc(rucActual);
  }, [rucActual]);

  const cargarListas = useCallback(async () => {
    if (!rucActual) return;
    setCargandoListas(true);
    try {
      // Códigos recientes (orderBy client-side: evita requerir índice compuesto)
      const qCodigos = query(
        collection(db, "bot_codigos_vinculacion"),
        where("ruc", "==", rucActual),
        limit(20)
      );
      const snapCodigos = await getDocs(qCodigos);
      const listaCodigos = snapCodigos.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      setCodigos(listaCodigos);

      // Vinculaciones activas de la empresa
      const qVinc = query(
        collection(db, "bot_vinculaciones"),
        where("rucEmpresa", "==", rucActual),
        limit(30)
      );
      const snapVinc = await getDocs(qVinc);
      const listaVinc = snapVinc.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      setVinculaciones(listaVinc);

      // Nombres de trabajadores vinculados (users/{dni})
      const dnis = Array.from(new Set(listaVinc.map((v) => String(v.dni || "")).filter(Boolean)));
      const nombresMap = {};
      await Promise.all(
        dnis.map(async (dniValor) => {
          try {
            const snap = await getDoc(doc(db, "users", dniValor));
            if (snap.exists()) {
              const u = snap.data();
              nombresMap[dniValor] = [u.apellidos, u.name].filter(Boolean).join(", ").trim();
            }
          } catch {
            // silencio: el nombre es informativo
          }
        })
      );
      setNombres(nombresMap);
    } catch (err) {
      console.error("Error cargando listas de Telegram:", err);
      setError("No se pudieron cargar los códigos y vinculaciones.");
    } finally {
      setCargandoListas(false);
    }
  }, [rucActual]);

  useEffect(() => {
    cargarListas();
  }, [cargarListas]);

  const generarCodigo = async (e, opciones = {}) => {
    e?.preventDefault?.();
    const confirmar = opciones.confirmar === true;
    const dniValor = opciones.dni || dni;
    const rucValor = opciones.ruc || ruc;
    setError("");
    setCodigoGenerado(null);

    if (!/^\d{8}$/.test(dniValor)) {
      setError("El DNI debe tener exactamente 8 dígitos.");
      return;
    }
    if (!rucValor) {
      setError("Falta el RUC de la empresa.");
      return;
    }

    setProcesando(true);
    setQrDataUrl("");
    setQrError(false);
    setTrabajador(null);
    try {
      const callable = httpsCallable(functions, "crearCodigoVinculacion");
      const res = await callable({ dni: dniValor, ruc: rucValor, confirmar });
      if (res.data.requiereConfirmacion) {
        // Trabajador ya vinculado: la callable NO creó código — mostrar Modal.
        setPendienteConfirmacion({
          dni: dniValor,
          ruc: rucValor,
          vinculadoAt: res.data.vinculacion?.vinculadoAt || null,
        });
        return;
      }
      // Deep link: el trabajador escanea el QR o toca el enlace y el bot
      // recibe "/start vincular-CODIGO" sin escribir nada.
      const link = BOT_USERNAME
        ? `https://t.me/${BOT_USERNAME}?start=vincular-${res.data.codigo}`
        : "";
      setCodigoGenerado({ ...res.data, link });
      if (link) {
        QRCode.toDataURL(link, { width: 320, margin: 1, color: { dark: "#0d4f3c", light: "#ffffff" } })
          .then(setQrDataUrl)
          .catch((err) => {
            console.error("Error generando QR:", err);
            setQrError(true);
          });
      }
      if (dniValor === dni) setDni("");
      cargarListas();
      // Datos del trabajador (users/{dni}) para el mensaje de WhatsApp.
      try {
        const snapTrab = await getDoc(doc(db, "users", dniValor));
        if (snapTrab.exists()) {
          const u = snapTrab.data();
          setTrabajador({
            nombre: [u.apellidos, u.name].filter(Boolean).join(", ").trim() || dniValor,
            phone: u.phone || "",
          });
        } else {
          setTrabajador({ nombre: dniValor, phone: "" });
        }
      } catch {
        setTrabajador({ nombre: dniValor, phone: "" });
      }
    } catch (err) {
      console.error("Error generando código:", err);
      const codigosError = {
        "permission-denied": "Solo administradores pueden generar códigos (claim admin requerido).",
        unauthenticated: "Debes iniciar sesión.",
        "not-found": err.message || "Trabajador no encontrado o RUC no coincide.",
        "invalid-argument": "Faltan datos requeridos (DNI y RUC).",
      };
      setError(codigosError[err.code] || err.message || "No se pudo generar el código.");
    } finally {
      setProcesando(false);
    }
  };

  const copiarCodigo = async () => {
    if (!codigoGenerado?.codigo) return;
    try {
      await navigator.clipboard.writeText(String(codigoGenerado.codigo));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setError("No se pudo copiar. Copia el código manualmente.");
    }
  };

  const waNumero = trabajador ? phoneWa(trabajador.phone) : "";
  const waTexto = mensajeWhatsapp({
    nombre: trabajador?.nombre || "trabajador",
    link: codigoGenerado?.link || "",
    expiraAt: codigoGenerado?.expiraAt,
  });
  const waUrl = codigoGenerado?.link
    ? waNumero
      ? `https://wa.me/${waNumero}?text=${encodeURIComponent(waTexto)}`
      : `https://wa.me/?text=${encodeURIComponent(waTexto)}`
    : "";

  return (
    <Container fluid className="py-4">
      <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
        {/* Header con degradado */}
        <Card.Header
          className="text-white py-4 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
            borderBottom: "3px solid #f59e0b",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
            <BotonManualHeader />
          </div>
          <div className="text-center">
            <h2 className="fw-bold mb-1">
              <FaTelegram className="me-3" />
              Marcación por Telegram
            </h2>
            <p className="mb-0 opacity-75">
              Genera códigos de vinculación para que los trabajadores marquen asistencia desde Telegram
            </p>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
          {error && (
            <Alert variant="danger" className="mb-4" dismissible onClose={() => setError("")}>
              <FaExclamationTriangle className="me-2" />
              {error}
            </Alert>
          )}

          <Row className="g-4 mb-4">
            {/* Generar código */}
            <Col lg={5}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold d-flex align-items-center mb-3">
                    <FaKey className="me-2 text-primary" /> Generar código de vinculación
                  </h5>
                  <Form onSubmit={generarCodigo}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold small text-uppercase opacity-75">
                        DNI del trabajador
                      </Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        placeholder="8 dígitos"
                        value={dni}
                        onChange={(e) => setDni(e.target.value.replace(/\D/g, "").slice(0, 8))}
                        style={{ borderRadius: "8px" }}
                      />
                      <Form.Text className="text-muted">
                        El trabajador debe existir en users y pertenecer a esta empresa.
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold small text-uppercase opacity-75">RUC empresa</Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        value={ruc}
                        onChange={(e) => setRuc(e.target.value.replace(/\D/g, "").slice(0, 11))}
                        style={{ borderRadius: "8px" }}
                      />
                    </Form.Group>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={procesando || !ruc}
                      className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #229ED9 0%, #2AABEE 100%)",
                        border: "none",
                        padding: "12px",
                      }}
                    >
                      {procesando ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" /> Generando...
                        </>
                      ) : (
                        <>
                          <FaKey /> Generar código
                        </>
                      )}
                    </Button>
                  </Form>
                  <div className="mt-3 small text-muted d-flex align-items-start">
                    <FaInfoCircle className="me-2 mt-1" />
                    <span>
                      El código caduca en <strong>24 horas</strong>, es de <strong>un solo uso</strong> y
                      solo puede generarlo un administrador.
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Resultado / instrucciones */}
            <Col lg={7}>
              {codigoGenerado ? (
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{
                    background: "linear-gradient(135deg, #f0f9ff, #e8f4fd)",
                    borderRadius: 14,
                  }}
                >
                  <Card.Body className="p-4 text-center">
                    <h6 className="fw-bold text-uppercase text-muted mb-3">
                      <FaCheckCircle className="me-2 text-success" />
                      Código generado
                    </h6>
                    <InputGroup className="justify-content-center mb-3">
                      <div
                        className="px-4 py-3 bg-white rounded-3 shadow-sm fw-bold text-center"
                        style={{
                          fontFamily: "'Courier New', monospace",
                          fontSize: "2.6rem",
                          letterSpacing: "0.45rem",
                          border: "2px dashed #229ED9",
                          minWidth: "260px",
                        }}
                      >
                        {codigoGenerado.codigo}
                      </div>
                      <Button
                        variant={copiado ? "success" : "outline-primary"}
                        onClick={copiarCodigo}
                        className="d-flex align-items-center gap-2 ms-2 fw-bold"
                        style={{ borderRadius: "10px" }}
                      >
                        {copiado ? <FaCheckCircle /> : <FaCopy />} {copiado ? "Copiado" : "Copiar"}
                      </Button>
                    </InputGroup>
                    <div className="small text-muted mb-3">
                      <FaClock className="me-1" />
                      Válido hasta: <strong>{formatearFecha(codigoGenerado.expiraAt)}</strong>
                    </div>
                    {codigoGenerado.link && (
                      <div className="mb-3 text-center">
                        {qrDataUrl ? (
                          <img
                            src={qrDataUrl}
                            alt="QR de vinculación para Telegram"
                            className="rounded shadow-sm mb-2"
                            style={{ width: 180, height: 180, background: "#fff" }}
                          />
                        ) : qrError ? (
                          <div className="small text-warning mb-2 fw-bold">
                            No se pudo generar el QR — usa el enlace de abajo (funciona igual, sin escribir nada).
                          </div>
                        ) : null}
                        <div className="small text-muted">
                          El trabajador solo debe escanear con la cámara del celular — o tocar el enlace:
                        </div>
                        <a
                          href={codigoGenerado.link}
                          target="_blank"
                          rel="noreferrer"
                          className="small user-select-all d-inline-block mt-1"
                        >
                          {codigoGenerado.link}
                        </a>
                      </div>
                    )}
                    {waUrl && (
                      <div className="mb-3 text-center">
                        <div className="d-flex justify-content-center gap-2 flex-wrap mb-2">
                          <Button
                            as="a"
                            href={waUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="d-flex align-items-center gap-2 fw-bold"
                            style={{
                              borderRadius: "10px",
                              background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                              border: "none",
                              padding: "10px 22px",
                            }}
                          >
                            <FaWhatsapp size="18px" /> Enviar por WhatsApp
                          </Button>
                          {qrDataUrl && (
                            <Button
                              as="a"
                              href={qrDataUrl}
                              download={`qr-vincular-${codigoGenerado.codigo}.png`}
                              variant="outline-success"
                              className="d-flex align-items-center gap-2"
                              style={{ borderRadius: "10px" }}
                            >
                              <FaDownload /> Descargar QR
                            </Button>
                          )}
                        </div>
                        <div className="small text-muted">
                          {waNumero ? (
                            <>
                              Se abrirá el chat de <strong>{trabajador?.nombre}</strong> (+51{" "}
                              {trabajador?.phone}) con el mensaje listo — solo presiona enviar.
                            </>
                          ) : (
                            <>
                              Sin teléfono registrado para {trabajador?.nombre || "este trabajador"}: se
                              abrirá WhatsApp para que elijas el chat.
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    <Alert variant="light" className="text-start border mb-0" style={{ borderRadius: 12 }}>
                      <h6 className="fw-bold mb-2">Instrucciones para el trabajador:</h6>
                      <ol className="mb-0 small">
                        <li>Abre Telegram y busca el bot de asistencia de la empresa.</li>
                        <li>
                          {codigoGenerado.link ? (
                            <>Escanea el QR o toca el enlace de arriba — el código se canjea solo, sin escribir nada.</>
                          ) : (
                            <>
                              Envía el comando:{" "}
                              <code className="user-select-all bg-white border px-2 py-1 rounded">
                                /vincular {codigoGenerado.codigo}
                              </code>
                            </>
                          )}
                        </li>
                        <li>
                          Espera la confirmación y marca con <code>/entrada</code> y <code>/salida</code>{" "}
                          desde el bot.
                        </li>
                      </ol>
                    </Alert>
                  </Card.Body>
                </Card>
              ) : (
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
                  <Card.Body className="p-4">
                    <h6 className="fw-bold text-muted mb-3">
                      <FaInfoCircle className="me-2" /> ¿Cómo funciona?
                    </h6>
                    <ol className="small text-muted mb-0">
                      <li>
                        <strong>RR.HH. genera un código</strong> para el trabajador (caduca en 24 h, un solo
                        uso).
                      </li>
                      <li>
                        El trabajador lo canjea una vez en Telegram con{" "}
                        <code>/vincular CÓDIGO</code>.
                      </li>
                      <li>
                        Después marca con <code>/entrada</code> y <code>/salida</code>; la ubicación se
                        valida contra la <strong>geocerca</strong> de la empresa antes de registrar.
                      </li>
                    </ol>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>

          {/* Códigos recientes */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
            <Card.Header
              className="d-flex justify-content-between align-items-center bg-white py-3"
              style={{ borderRadius: "14px 14px 0 0" }}
            >
              <h5 className="fw-bold mb-0 d-flex align-items-center">
                <FaKey className="me-2 text-primary" /> Códigos recientes
              </h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={cargarListas}
                disabled={cargandoListas}
                className="d-flex align-items-center gap-2"
                style={{ borderRadius: "8px" }}
              >
                <FaSyncAlt className={cargandoListas ? "fa-spin" : ""} /> Actualizar
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {cargandoListas && codigos.length === 0 ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : codigos.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FaKey className="mb-2 opacity-25" size="32px" />
                  <p className="mb-0">Aún no se han generado códigos para esta empresa.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle bg-white mb-0">
                    <thead style={{ background: "#f8f9fa" }}>
                      <tr>
                        <th className="text-center py-3">#</th>
                        <th className="py-3">Código</th>
                        <th className="py-3">DNI</th>
                        <th className="py-3">Creado</th>
                        <th className="py-3">Expira</th>
                        <th className="text-center py-3">Estado</th>
                        <th className="py-3">Creado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codigos.map((codigo, index) => {
                        const estado = estadoCodigo(codigo);
                        return (
                          <tr key={codigo.id}>
                            <td className="text-center fw-bold">{index + 1}</td>
                            <td>
                              <span
                                className="fw-bold text-primary user-select-all"
                                style={{ fontFamily: "'Courier New', monospace", letterSpacing: "0.1rem" }}
                              >
                                {codigo.id}
                              </span>
                            </td>
                            <td className="fw-bold">{codigo.dni}</td>
                            <td className="text-muted">{formatearFecha(codigo.createdAt)}</td>
                            <td className="text-muted">{formatearFecha(codigo.expiraAt)}</td>
                            <td className="text-center">
                              <Badge bg={estado.variante} className="px-3 py-2 rounded-pill shadow-sm">
                                {estado.etiqueta}
                              </Badge>
                            </td>
                            <td className="text-muted small">{codigo.creadoPor || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Trabajadores vinculados */}
          <Card className="border-0 shadow-sm" style={{ borderRadius: 14 }}>
            <Card.Header className="bg-white py-3" style={{ borderRadius: "14px 14px 0 0" }}>
              <h5 className="fw-bold mb-0 d-flex align-items-center">
                <FaUsers className="me-2 text-success" /> Trabajadores vinculados ({vinculaciones.length})
              </h5>
            </Card.Header>
            <Card.Body className="p-0">
              {cargandoListas && vinculaciones.length === 0 ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="success" />
                </div>
              ) : vinculaciones.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FaUsers className="mb-2 opacity-25" size="32px" />
                  <p className="mb-0">Ningún trabajador tiene Telegram vinculado todavía.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle bg-white mb-0">
                    <thead style={{ background: "#f8f9fa" }}>
                      <tr>
                        <th className="text-center py-3">#</th>
                        <th className="py-3">DNI</th>
                        <th className="py-3">Trabajador</th>
                        <th className="py-3">Canal</th>
                        <th className="py-3">Vinculado</th>
                        <th className="py-3">Código usado</th>
                        <th className="text-center py-3">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vinculaciones.map((vinc, index) => (
                        <tr key={vinc.id}>
                          <td className="text-center fw-bold">{index + 1}</td>
                          <td className="fw-bold text-primary">{vinc.dni}</td>
                          <td>{nombres[vinc.dni] || "—"}</td>
                          <td className="text-muted">{vinc.canal || "telegram"}</td>
                          <td className="text-muted">{formatearFecha(vinc.createdAt)}</td>
                          <td className="text-muted small user-select-all">{vinc.vinculadoPorCodigo || "—"}</td>
                          <td className="text-center">
                            {vinc.estado === "activo" ? (
                              <Badge bg="success" className="px-3 py-2 rounded-pill shadow-sm">
                                <FaTelegram className="me-1" /> Activo
                              </Badge>
                            ) : (
                              <Badge bg="secondary" className="px-3 py-2 rounded-pill shadow-sm">
                                {vinc.estado || "Inactivo"}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Advertencia: trabajador ya vinculado (la callable no creó código) */}
      <Modal
        show={!!pendienteConfirmacion}
        onHide={() => setPendienteConfirmacion(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FaExclamationTriangle className="me-2 text-warning" />
            Este trabajador ya tiene Telegram activo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            El DNI <strong>{pendienteConfirmacion?.dni}</strong> ya está vinculado
            {pendienteConfirmacion?.vinculadoAt && (
              <>
                {" "}el <strong>{formatearFecha(pendienteConfirmacion.vinculadoAt)}</strong>
              </>
            )}
            . Generar un código nuevo solo es necesario si{" "}
            <strong>cambió de celular o número</strong> — al vincular de nuevo, la
            cuenta anterior deja de poder marcar.
          </p>
          <p className="text-muted small mb-0">
            ¿Generar el código de todos modos?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setPendienteConfirmacion(null)}
            style={{ borderRadius: "10px" }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const pend = pendienteConfirmacion;
              setPendienteConfirmacion(null);
              if (pend) generarCodigo(null, { ...pend, confirmar: true });
            }}
            style={{
              borderRadius: "10px",
              background: "linear-gradient(135deg, #229ED9 0%, #2AABEE 100%)",
              border: "none",
            }}
          >
            Sí, generar código
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default TelegramAsistencia;
